import { IconButton, Typography } from '@mui/joy';
import { DownloadSimple, X } from '@phosphor-icons/react';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import PDFViewers from './PdfViewer';

import { getAttachmentIcon, getFileTypeCategory } from '../constants';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ReportService } from '~/services/api/reports/endpoints';
import { ReportUploadedFile } from '~/services/api/reports/types';
import { useWrappedMutation } from '~/services/api/rest-api';

export function ExpandableAttachment({
  file,
  isAllowedToDownload,
}: Readonly<{ file: ReportUploadedFile; isAllowedToDownload: boolean }>) {
  const intl = useIntl();
  const slug = useTenantSlug();
  const reportId = z.coerce.string().catch('').parse(useParams().reportId);
  const { createToastNotification } = useToastNotificationContext();
  const [isHovered, setIsHovered] = useState(false);

  const downloadReportFile = useWrappedMutation(
    ReportService.endpoints.downloadReportFile.useMutation(),
  );

  const downloadAttachment = async () => {
    if (isAllowedToDownload) {
      downloadReportFile.mutateAsync(
        {
          file: file,
          report_reference: reportId,
          slug: slug,
        },
        {
          onSuccess: (data) => {
            const downloadUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', file.original_file_name);
            document.body.appendChild(link);
            link.click();
          },
        },
      );
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'You are not allowed to download attachments',
        }),
        'error',
      );
    }
  };

  return (
    <ViewAttachmentModal file={file} downloadAttachment={downloadAttachment}>
      <div
        className='flex flex-col justify-between items-center cursor-pointer hover:bg-slate-200 p-2 relative'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          className='h-20'
          src={getAttachmentIcon(file.file_type, file.url)}
          alt={file.original_file_name}
        />
        <div className='text-ellipsis overflow-hidden max-w-32'>
          {file.original_file_name}
        </div>
        {isHovered && (
          <IconButton
            variant='soft'
            className='!absolute top-1/3 right-1/2 translate-x-1/2 -translate-y-1/3'
            onClick={(e) => {
              e.stopPropagation();
              downloadAttachment();
            }}
            loading={downloadReportFile.state.isLoading}
          >
            <DownloadSimple />
          </IconButton>
        )}
      </div>
    </ViewAttachmentModal>
  );
}

export function ViewAttachmentModal({
  file,
  downloadAttachment,
  children,
}: Readonly<{
  file: {
    url: string;
    original_file_name: string;
    file_type: string;
  };
  downloadAttachment: () => void;
  children: ReactNode;
}>) {
  const fileTypeCategory = getFileTypeCategory(file.file_type);
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent size='full'>
        <div className='flex justify-between items-center gap-2'>
          <DialogTitle>{file.original_file_name}</DialogTitle>
          <div className='flex gap-2'>
            <IconButton
              variant='soft'
              onClick={(e) => {
                e.stopPropagation();
                downloadAttachment();
              }}
            >
              <DownloadSimple />
            </IconButton>
            <DialogClose asChild>
              <IconButton variant='plain'>
                <X />
              </IconButton>
            </DialogClose>
          </div>
        </div>
        <div className='min-h-[500px] flex justify-center items-center'>
          {fileTypeCategory === 'img' && (
            <img
              className='h-[800px]'
              src={file.url}
              alt={file.original_file_name}
              style={{ objectFit: 'contain' }}
            />
          )}
          {fileTypeCategory === 'video' && (
            <video className='w-full p-8' controls controlsList='nodownload'>
              <source src={file.url} type='video/mp4' />
              <track kind='captions' srcLang='en' label='English' default />
              <FormattedMessage defaultMessage='Your browser does not support the video tag.' />
            </video>
          )}
          {fileTypeCategory === 'pdf' && <PDFViewers fileName={file.url} />}
          {fileTypeCategory === 'document' && (
            <Typography>
              <FormattedMessage
                defaultMessage='Cant preview this file. {link} to download'
                values={{
                  link: (
                    <Button variant='plain' onClick={downloadAttachment}>
                      <FormattedMessage defaultMessage='Click here' />
                    </Button>
                  ),
                }}
              />
            </Typography>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
